/**
 * CREDIT NOTES AGAINST CERTIFIED BILLS.
 *
 * A running account bill has been approved, and then something turns out to be
 * wrong with it: work certified for more than was done, cement issued from
 * OVAL's own stores that has to come back off his account, a penalty under the
 * contract, a rate applied wrongly.
 *
 * WHY THIS EXISTS — board instruction, 21-09-2026
 *
 * Until 0165 the only answer was "it will come right on the next bill". That
 * is true — RA bills are cumulative, so an over-certification corrects itself
 * the moment the next one is raised — but it leaves a swing on that bill which
 * nothing on the file explains, and the contractor is entitled to be told why
 * he is getting less. Worse: if there is no next bill, it never comes right at
 * all, and the order closes with the error inside it.
 *
 * WHAT IT WILL NOT DO
 *
 * Take a bill below what the contractor has ALREADY BEEN PAID. Money out of
 * the door cannot be credited back — it has to be recovered, which is the next
 * bill or a different conversation entirely. rpc_crn_raise refuses it and says
 * so; this screen shows the ceiling before anybody types.
 *
 * HOW IT REACHES THE MONEY
 *
 * An approved credit note reduces what the payable owes. fn_contractor_payment_
 * sync counts it alongside the cash, so a payable settled partly by a credit
 * note reaches PAID and the work order can be closed. Before 0165 it could not
 * — the payable would have sat OPEN for ever and 0156 would have refused the
 * closure for ever with it.
 */
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, FilterX, Receipt, Scissors, Send, Trash2,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { dbMessage, useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, inr, rupees } from '@/lib/format'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, SearchInput, Select,
  Stat, StatusPill, Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { ApprovalChain } from '../components/ApprovalChain'

const n = (v: unknown) => Number(v ?? 0) || 0

/** Mirrors rpc_crn_raise. The RPC is the enforcement; this is the door. */
const MAY_RAISE = ['admin', 'accounts_spoc', 'project_head', 'project_manager',
                   'site_construction_manager', 'procurement_head']

export const CRN_KINDS = [
  { value: 'OVER_CERTIFIED',    label: 'Work certified for more than was done' },
  { value: 'MATERIAL_SUPPLIED', label: 'Material supplied by OVAL, recoverable' },
  { value: 'PENALTY',           label: 'Penalty under the contract' },
  { value: 'RATE_CORRECTION',   label: 'Rate applied wrongly' },
  { value: 'OTHER',             label: 'Other' },
]

export interface CrnRow {
  id: string; ra_bill_id: string; ra_no: number; payable_id: string | null
  wo_id: string; wo_title: string | null
  site_id: string; site_name: string | null
  vendor_id: string; vendor_name: string | null
  amount: number; kind: string; kind_label: string
  reason: string; remarks: string | null
  bill_net_at_raising: number; bill_net_now: number; bill_credited_total: number
  status: string; current_step: number
  created_by: string | null; raised_by_name: string | null; creator_email: string | null
  approved_by: string | null; approved_by_name: string | null
  approval_date: string | null
  return_reason: string | null; rejection_reason: string | null
  date_created: string; created_at: string
}

/* ========================================================================== */

const BLANK = { term: '', status: '', kind: '' }

export function CreditNoteList() {
  const [f, setF] = useState({ ...BLANK })
  const { sort, toggle } = useSort('date_created', 'desc')
  const set = (patch: Partial<typeof BLANK>) => setF((v) => ({ ...v, ...patch }))
  const dirty = Object.values(f).some(Boolean)

  const { data: rows, loading } = useQuery<CrnRow>(
    'v_contractor_credit_notes',
    () => supabase.from('v_contractor_credit_notes').select('*')
      .order('created_at', { ascending: false }).limit(1000),
    [], { cacheable: false })

  const shown = useMemo(() => {
    const t = f.term.trim().toLowerCase()
    return rows.filter((r) =>
      (!f.status || r.status === f.status)
      && (!f.kind || r.kind === f.kind)
      && (!t || r.id.toLowerCase().includes(t) || r.wo_id.toLowerCase().includes(t)
          || r.ra_bill_id.toLowerCase().includes(t)
          || (r.vendor_name ?? '').toLowerCase().includes(t)
          || (r.reason ?? '').toLowerCase().includes(t)))
  }, [rows, f])

  const sorted = useMemo(() => sortRows(shown, sort), [shown, sort])
  const approved = rows.filter((r) => r.status === 'APPROVED')
  const inChain = rows.filter((r) => ['DRAFT', 'SUBMITTED'].includes(r.status))

  if (loading) return <Loading label="Reading the credit notes…" />

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Credit Notes</h1>
        <p className="text-sm text-slate-500">
          Money taken back off a running account bill that has already been certified.
          Numbered <strong>CRN-</strong>, agreed by the Project Manager and signed by the
          Project Head.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Stat label="Credited, all time" value={rupees(approved.reduce((a, r) => a + n(r.amount), 0))}
              sub={`${approved.length} note(s) approved`}
              icon={<Scissors className="h-4 w-4" />} />
        <Stat label="In the chain" value={inChain.length}
              tone={inChain.length ? 'warn' : 'default'}
              sub={inChain.length
                ? `${rupees(inChain.reduce((a, r) => a + n(r.amount), 0))} not yet signed`
                : 'nothing waiting'}
              icon={<Receipt className="h-4 w-4" />} />
        <Stat label="Contractors affected"
              value={new Set(approved.map((r) => r.vendor_id)).size}
              icon={<AlertTriangle className="h-4 w-4" />} />
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <Scissors className="mr-1 inline h-3.5 w-3.5" />
        A credit note is raised <strong>on the bill</strong>, where the certified figure and
        what has already been paid against it are both on screen — open any RA bill and use
        the credit notes panel. This is the register.
      </div>

      <Card
        title="Every credit note"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput value={f.term} onChange={(v) => set({ term: v })}
                         placeholder="CRN, bill, work order, contractor…" />
            <Select className="w-auto" value={f.status}
                    onChange={(e) => set({ status: e.target.value })}
                    placeholder="Any status"
                    options={['DRAFT', 'SUBMITTED', 'APPROVED', 'RETURNED', 'REJECTED', 'CANCELLED']
                      .map((v) => ({ value: v, label: v }))} />
            <Select className="w-auto" value={f.kind}
                    onChange={(e) => set({ kind: e.target.value })}
                    placeholder="Any kind" options={CRN_KINDS} />
            {dirty && (
              <button className="btn-ghost btn-sm" onClick={() => setF({ ...BLANK })}>
                <FilterX className="h-4 w-4" /> Clear
              </button>
            )}
          </div>
        }
      >
        {sorted.length === 0 ? (
          <EmptyState icon={<Scissors className="h-8 w-8" />}
                      title={dirty ? 'Nothing matches' : 'No credit note has been raised'}
                      message={dirty
                        ? 'Clear the filters to see everything.'
                        : 'Open a certified RA bill and use the credit notes panel at the foot of it.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Note</Th>
                  <Th id="date_created" sort={sort} onSort={toggle}>Raised</Th>
                  <th>Against</th>
                  <Th id="vendor_name" sort={sort} onSort={toggle}>Contractor</Th>
                  <Th id="amount" sort={sort} onSort={toggle} align="right">Amount</Th>
                  <th>Why</th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap font-medium">
                      <Link to={`/service/credit-notes/${r.id}`}
                            className="text-amber-700 hover:underline">{r.id}</Link>
                    </td>
                    <td className="whitespace-nowrap text-xs">{ddmmyyyy(r.date_created)}</td>
                    <td className="whitespace-nowrap text-xs">
                      <Link to={`/service/ra-bills/${r.ra_bill_id}`}
                            className="font-medium text-amber-700 hover:underline">
                        RA-{r.ra_no}
                      </Link>
                      <Link to={`/service/work-orders/${r.wo_id}`}
                            className="block text-[11px] text-slate-400 hover:text-amber-700 hover:underline">
                        {r.wo_id}
                      </Link>
                    </td>
                    <td className="max-w-[150px] truncate">{r.vendor_name}</td>
                    <td className="num whitespace-nowrap font-semibold text-red-700">
                      −{rupees(r.amount).replace('₹ ', '₹ ')}
                    </td>
                    <td className="max-w-[240px] truncate text-xs text-slate-500" title={r.reason}>
                      <span className="block font-medium text-slate-600">{r.kind_label}</span>
                      {r.reason}
                    </td>
                    <td><StatusPill status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

/* ========================================================================== */

export function CreditNoteDetail() {
  const { id = '' } = useParams()
  const nav = useNavigate()
  const { profile } = useAuth()
  const toast = useToast()
  const [withdrawing, setWithdrawing] = useState(false)
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)

  const { data: rows, loading, refresh } = useQuery<CrnRow>(
    `crn_${id}`,
    () => supabase.from('v_contractor_credit_notes').select('*').eq('id', id).limit(1),
    [id], { cacheable: false })

  if (loading) return <Loading label="Reading the credit note…" />
  const c = rows[0]
  if (!c) {
    return <EmptyState icon={<AlertTriangle className="h-8 w-8" />} title="No such credit note"
                       message={`Nothing here is called ${id}.`} />
  }

  const isDraft = c.status === 'DRAFT' || c.status === 'RETURNED'
  const canRaise = MAY_RAISE.includes(profile?.role ?? '')
  const iRaisedIt = !!c.created_by && c.created_by === profile?.id
  const canWithdraw = isDraft && (iRaisedIt || ['admin', 'accounts_spoc'].includes(profile?.role ?? ''))

  const act = async (fn: () => PromiseLike<{ error: unknown }>, done: string) => {
    setBusy(true)
    const { error } = await fn()
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'That did not go through.')); return }
    setWithdrawing(false); setWhy(''); refresh(); toast.success(done)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button className="btn-ghost btn-sm mb-1" onClick={() => nav('/service/credit-notes')}>
            <ArrowLeft className="h-4 w-4" /> All credit notes
          </button>
          <h1 className="text-xl font-bold text-slate-900">{c.id}</h1>
          <p className="text-sm text-slate-500">
            {rupees(c.amount)} off{' '}
            <Link to={`/service/ra-bills/${c.ra_bill_id}`} className="text-amber-700 hover:underline">
              RA-{c.ra_no}
            </Link>{' '}on{' '}
            <Link to={`/service/work-orders/${c.wo_id}`} className="text-amber-700 hover:underline">
              {c.wo_id}
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={c.status} />
          {isDraft && canWithdraw && (
            <button className="btn-secondary btn-sm" disabled={busy}
                    onClick={() => { setWhy(''); setWithdrawing(true) }}>
              <Trash2 className="h-4 w-4" /> Withdraw
            </button>
          )}
          {isDraft && canRaise && (
            <button className="btn-primary btn-sm" disabled={busy}
                    onClick={() => void act(
                      () => supabase.rpc('rpc_works_action',
                        { p_doc_type: 'CRN', p_doc_id: c.id, p_action: 'SUBMIT' }),
                      'Sent to the Project Manager.')}>
              <Send className="h-4 w-4" /> {busy ? 'Sending…' : 'Submit for approval'}
            </button>
          )}
        </div>
      </div>

      {isDraft && (
        <Alert tone={c.status === 'RETURNED' ? 'warning' : 'info'}
               title={c.status === 'RETURNED' ? 'Sent back to you' : 'This is a draft'}>
          {c.status === 'RETURNED'
            ? 'Deal with what the Project Manager said, then submit it again.'
            : 'Nothing comes off the bill until the Project Manager and the Project Head have both agreed it.'}
        </Alert>
      )}

      {c.status === 'APPROVED' && (
        <Alert tone="success" title="Agreed, and taken off the payable">
          {rupees(c.amount)} is no longer owed on RA-{c.ra_no}. If that settles the payable
          in full it will show as PAID, and the work order can be closed.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card title="The money">
            <div className="grid gap-3 sm:grid-cols-4">
              <Figure label="Credited" value={rupees(c.amount)} strong tone="bad" />
              <Figure label="The bill, when raised" value={rupees(c.bill_net_at_raising)} />
              <Figure label="The bill now" value={rupees(c.bill_net_now)} />
              <Figure label="Credited on it, all notes" value={rupees(c.bill_credited_total)} />
            </div>
          </Card>

          <Card title="Why">
            <p className="text-sm font-medium text-slate-700">{c.kind_label}</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{c.reason}</p>
            {c.remarks && <p className="mt-2 text-xs text-slate-500">{c.remarks}</p>}
            <p className="mt-3 text-xs text-slate-400">
              Raised by {c.raised_by_name ?? 'somebody no longer on the system'}{' '}
              on {ddmmyyyy(c.date_created)}.
              {c.approved_by_name && ` Signed by ${c.approved_by_name}.`}
            </p>
            {c.return_reason && <Alert tone="warning" title="Returned">{c.return_reason}</Alert>}
            {c.rejection_reason && <Alert tone="error" title="Rejected">{c.rejection_reason}</Alert>}
          </Card>

          <ApprovalChain docType="CRN" docId={c.id} status={c.status}
                         currentStep={c.current_step} createdBy={c.created_by}
                         onChanged={refresh} />
        </div>

        <div className="space-y-4">
          <Card title="Against">
            <dl className="space-y-2 text-sm">
              <Row label="Bill" value={
                <Link to={`/service/ra-bills/${c.ra_bill_id}`} className="text-amber-700 hover:underline">
                  RA-{c.ra_no}
                </Link>} />
              <Row label="Work order" value={
                <Link to={`/service/work-orders/${c.wo_id}`} className="text-amber-700 hover:underline">
                  {c.wo_id}
                </Link>} />
              <Row label="Title" value={c.wo_title ?? '—'} />
              <Row label="Site" value={c.site_name ?? c.site_id} />
              <Row label="Contractor" value={c.vendor_name ?? c.vendor_id} />
              {c.payable_id && (
                <Row label="Payable" value={
                  <Link to={`/service/contractor-payables/${c.payable_id}`}
                        className="text-amber-700 hover:underline">{c.payable_id}</Link>} />
              )}
            </dl>
          </Card>
        </div>
      </div>

      <Modal open={withdrawing} onClose={() => setWithdrawing(false)}
             title="Withdraw this credit note" subtitle={`${c.id} — ${rupees(c.amount)}`}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setWithdrawing(false)} disabled={busy}>
                   Keep it
                 </button>
                 <button className="btn-warning" disabled={busy}
                         onClick={() => void act(
                           () => supabase.rpc('rpc_crn_withdraw',
                             { p_id: c.id, p_reason: why.trim() || null }),
                           'Withdrawn.')}>
                   {busy ? 'Withdrawing…' : 'Withdraw it'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <Alert tone="info" title="Nothing is deleted">
            It stays on the register as withdrawn, and the {rupees(c.amount)} is no longer
            credited against RA-{c.ra_no}.
          </Alert>
          <Field label="Why" hint="Optional.">
            <Textarea rows={3} value={why} onChange={(e) => setWhy(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}

/* ========================================================================== */
/*  RAISING ONE — mounted on the RA bill, where the figures are               */
/* ========================================================================== */

export function RaiseCreditNote({ bill, onDone }: {
  bill: { id: string; ra_no: number; net_payable: number; paid_to_date: number; wo_id: string }
  onDone: () => void
}) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState('')
  const [kind, setKind] = useState('OVER_CERTIFIED')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const { data: existing, refresh } = useQuery<CrnRow>(
    `crn_for_${bill.id}`,
    () => supabase.from('v_contractor_credit_notes').select('*').eq('ra_bill_id', bill.id),
    [bill.id], { cacheable: false })

  const live = existing.filter((r) => !['REJECTED', 'CANCELLED'].includes(r.status))
  const credited = live.reduce((a, r) => a + n(r.amount), 0)

  // The RPC refuses a credit that would take the bill below what has already
  // been paid. Show that ceiling rather than letting somebody find it.
  const ceiling = Math.max(
    Math.min(n(bill.net_payable) - credited, n(bill.net_payable) - n(bill.paid_to_date) - credited),
    0)
  const asked = Number(amount) || 0
  const over = asked > ceiling + 0.004
  const ready = asked > 0 && !over && reason.trim().length > 0

  const submit = async () => {
    setBusy(true)
    const { error } = await supabase.rpc('rpc_crn_raise', {
      p_ra_id: bill.id, p_amount: asked, p_kind: kind, p_reason: reason.trim(),
    })
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'Could not raise the credit note.')); return }
    setOpen(false); setAmount(''); setReason('')
    refresh(); onDone(); toast.success('Credit note raised.')
  }

  return (
    <Card title="Credit notes on this bill"
          subtitle="Money taken back off a bill that has already been certified."
          actions={
            ceiling > 0.5 ? (
              <button className="btn-secondary btn-sm" onClick={() => setOpen(true)}>
                <Scissors className="h-4 w-4" /> Raise a credit note
              </button>
            ) : undefined
          }>
      {live.length === 0 ? (
        <p className="text-sm text-slate-500">
          None. {ceiling > 0.5
            ? `Up to ${rupees(ceiling)} could be credited against this bill.`
            : 'Nothing can be credited — the bill has been paid in full, so anything to recover goes on the next one.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {live.map((r) => (
            <li key={r.id} className="flex items-start justify-between gap-3 rounded-lg
                                      border border-slate-200 bg-slate-50 px-3 py-2">
              <span className="min-w-0 flex-1 text-sm">
                <Link to={`/service/credit-notes/${r.id}`}
                      className="font-medium text-amber-700 hover:underline">{r.id}</Link>
                <span className="ml-2 text-slate-600">{r.kind_label}</span>
                <span className="block truncate text-xs text-slate-500">{r.reason}</span>
              </span>
              <span className="whitespace-nowrap text-right">
                <span className="block font-semibold text-red-700">−{rupees(r.amount)}</span>
                <StatusPill status={r.status} />
              </span>
            </li>
          ))}
        </ul>
      )}

      <Modal open={open} onClose={() => setOpen(false)}
             title={`Credit note against RA-${bill.ra_no}`}
             subtitle={bill.wo_id}
             footer={
               <>
                 <button className="btn-ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
                 <button className="btn-primary" onClick={submit} disabled={!ready || busy}>
                   {busy ? 'Raising…' : 'Raise it'}
                 </button>
               </>
             }>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Figure label="The bill" value={rupees(bill.net_payable)} />
            <Figure label="Already paid" value={rupees(bill.paid_to_date)} />
            <Figure label="You can credit up to" value={rupees(ceiling)} tone="warn" />
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            A credit note cannot take the bill below what the contractor has already been
            paid. Money out of the door has to be recovered on the next bill, not credited
            back off this one.
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Amount" required
                   error={over ? `Only ${rupees(ceiling)} can be credited here.` : undefined}>
              <Input type="number" min="0" step="0.01" value={amount} invalid={over}
                     onChange={(e) => setAmount(e.target.value)} placeholder={inr(ceiling)} />
            </Field>
            <Field label="What kind" required>
              <Select value={kind} onChange={(e) => setKind(e.target.value)} options={CRN_KINDS} />
            </Field>
          </div>

          <Field label="Why" required
                 hint="The contractor will ask. This is the answer, and it is kept permanently.">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Cement issued from OVAL stores against this reach — 40 bags at the contract recovery rate." />
          </Field>
        </div>
      </Modal>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */

function Figure({ label, value, strong, tone }: {
  label: string; value: string; strong?: boolean; tone?: 'warn' | 'bad'
}) {
  return (
    <div className={`rounded-lg border p-3 ${
      tone === 'warn' ? 'border-amber-200 bg-amber-50'
      : tone === 'bad' ? 'border-red-200 bg-red-50'
      : 'border-slate-200 bg-slate-50'}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`${strong ? 'text-lg font-bold' : 'text-sm font-semibold'} text-slate-900`}>
        {value}
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-right text-sm text-slate-800">{value}</dd>
    </div>
  )
}

export default CreditNoteList
